import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';
import { formatAlarmInterval } from './alarm-interval';
import {
  dataWindowSecondsToValue,
  dataWindowUnitLabels,
  formatDataWindowValue,
  getDataWindowUnit,
} from '../filterParameterMapping';

// Reuses the Angular formatTimestamp filter for exact parity with the rest of the app (FilterPresets.js does the same).
const formatTimestamp = value => {
  if (!value) return '-';
  try {
    const injector = angular.element('.ng-scope').injector();
    const filter = injector?.get('formatTimestampFilter');
    if (filter) return filter(value);
  } catch (e) {
    // fallback if injector or filter unavailable
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

//const getRuleUpdatedTs = rule => rule.updatedTs || null;
const getRuleCreatedTs = rule => rule.createdTs || null;

const formatThreshold = rule =>
  `${rule.alarmThresholdCount} in ${formatAlarmInterval(rule.alarmIntervalSeconds)}`;

const formatDataWindow = dataWindowSeconds => {
  const seconds = Number(dataWindowSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return '-';
  const unit = getDataWindowUnit(seconds);
  const value = formatDataWindowValue(dataWindowSecondsToValue(seconds, unit));
  return `${value} ${dataWindowUnitLabels[unit]}`;
};

const getRuleEventIds = rule => {
  if (Array.isArray(rule.eventNumbers) && rule.eventNumbers.length > 0) {
    return `[${rule.eventNumbers.join(', ')}]`;
  }
  return '-';
};

const getStatusToggleTitle = rule => {
  if (rule.archivedTs) return 'Archived rules cannot be activated or deactivated';
  return rule.isActive ? 'Deactivate' : 'Activate';
};

const getColumns = ({ onEdit, onArchive, onToggleActive, onRunReport, pendingRuleActions }) => [
  { accessorKey: 'id', header: 'ID', size: 80, grow: false },
  {
    accessorKey: 'ruleName',
    header: 'Name',
    size: 160,
    Cell: ({ row }) => <span data-testid={`alarm-rule-name-${row.original.id}`}>{row.original.ruleName}</span>,
  },
  {
    id: 'ruleType',
    header: 'Type',
    size: 120,
    grow: false,
    accessorFn: rule => rule.ruleType || '',
    sortingFn: 'text',
    sortDescFirst: false,
    Cell: ({ row }) => <span title={row.original.ruleType} data-testid={`alarm-rule-type-${row.original.id}`}>{row.original.ruleType || '-'}</span>,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 140,
    grow: false,
    Cell: ({ row }) => <span title={row.original.description} data-testid={`alarm-rule-description-${row.original.id}`}>{row.original.description}</span>,
  },
  {
    id: 'threshold',
    header: 'Threshold',
    size: 140,
    accessorFn: formatThreshold,
  },
  {
    id: 'data-window',
    header: 'Data Window',
    size: 160,
    accessorFn: rule => {
      const seconds = Number(rule.dataWindowSeconds);
      return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    },
    Cell: ({ row }) => formatDataWindow(row.original.dataWindowSeconds),
  },
  {
    id: 'isActive',
    header: 'Status',
    size: 90,
    accessorFn: rule => (rule.isActive ? 'Active' : 'Inactive'),
    Cell: ({ row }) => (
      <span className={`alarm-rule-table__status ${row.original.isActive ? 'is-active' : 'is-inactive'}`} data-testid={`alarm-rule-status-${row.original.id}`}>
        {row.original.isActive ? 'Active' : 'Inactive'}
      </span>
    ),
  },
  {
    id: 'createdTs',
    header: 'Created',
    size: 190,
    accessorFn: rule => {
      const timestamp = DateTime.fromISO(getRuleCreatedTs(rule) || '', { zone: 'utc' }).toMillis();
      return Number.isFinite(timestamp) ? timestamp : undefined;
    },
    sortingFn: 'basic',
    sortDescFirst: true,
    Cell: ({ row }) => <span data-testid={`alarm-rule-created-${row.original.id}`}>{formatTimestamp(getRuleCreatedTs(row.original))}</span>,
  },
  /* {
    id: 'updatedTs',
    header: 'Updated',
    size: 130,
    accessorFn: rule => formatTimestamp(getRuleUpdatedTs(rule)),
  }, */
  {
    id: 'eventNumbers',
    header: 'Event IDs',
    size: 130,
    accessorFn: getRuleEventIds,
  },
  {
    accessorKey: 'version',
    header: 'Version',
    size: 80,
    accessorFn: rule => (rule.version != null ? `v${rule.version}` : '-'),
  },
  {
    id: 'owner',
    header: 'Owner',
    size: 140,
    accessorFn: rule => rule.ownerText || 'Unassigned',
    //accessorFn: rule => rule.owner?.name || 'Unassigned',
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 190,
    enableSorting: false,
    enableHiding: false,
    Cell: ({ row }) => {
      const pendingAction = pendingRuleActions.get(row.original.id);
      const isPending = !!pendingAction;
      const isStatusPending = pendingAction === 'status';
      const isArchivePending = pendingAction === 'archive';
      const archiveLabel = row.original.archivedTs ? 'Unarchive' : 'Archive';
      const statusLabel = isStatusPending ? 'Updating status' : getStatusToggleTitle(row.original);
      const archiveActionLabel = isArchivePending ? 'Updating archive status' : archiveLabel;
      const statusIcon = row.original.isActive ? (
        <ToggleOnIcon fontSize="small" color={row.original.archivedTs ? 'disabled' : 'primary'} />
      ) : (
        <ToggleOffIcon fontSize="small" />
      );
      const archiveIcon = row.original.archivedTs ? (
        <UnarchiveOutlinedIcon fontSize="small" />
      ) : (
        <ArchiveOutlinedIcon fontSize="small" />
      );

      return (
        <span className="alarm-rule-table__actions">
          <Tooltip title={row.original.archivedTs ? 'Archived rules cannot be edited' : 'Edit'}>
            <span>
              <IconButton
                size="small"
                disabled={!!row.original.archivedTs || isPending}
                onClick={event => {
                  event.stopPropagation();
                  onEdit(row.original);
                }}
                data-testid={`alarm-rule-edit-button-${row.original.id}`}
                aria-label="Edit"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={statusLabel}>
            <span>
              <IconButton
                size="small"
                disabled={!!row.original.archivedTs || isPending}
                onClick={event => {
                  event.stopPropagation();
                  onToggleActive(row.original);
                }}
                data-testid={`alarm-rule-status-toggle-${row.original.id}`}
                aria-label={statusLabel}
                aria-busy={isStatusPending}
              >
                {isStatusPending ? (
                  <CircularProgress
                    size={20}
                    aria-label="Updating rule status"
                    data-testid={`alarm-rule-status-toggle-progress-${row.original.id}`}
                  />
                ) : statusIcon}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={archiveActionLabel}>
            <span>
              <IconButton
                size="small"
                disabled={isPending}
                onClick={event => {
                  event.stopPropagation();
                  onArchive(row.original);
                }}
                data-testid={`alarm-rule-archive-button-${row.original.id}`}
                aria-label={archiveActionLabel}
                aria-busy={isArchivePending}
              >
                {isArchivePending ? (
                  <CircularProgress
                    size={20}
                    aria-label="Updating rule archive status"
                    data-testid={`alarm-rule-archive-progress-${row.original.id}`}
                  />
                ) : archiveIcon}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Run Report">
            <span>
              <IconButton
                size="small"
                disabled={isPending}
                onClick={event => {
                  event.stopPropagation();
                  onRunReport(row.original);
                }}
                data-testid={`alarm-rule-run-report-button-${row.original.id}`}
                aria-label="Run Report"
              >
                <AssessmentOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      );
    },
  },
];

export const AlarmRuleTable = ({
  rules = [],
  isActive = false,
  loadBusy = false,
  showArchived = false,
  onShowArchivedChange,
  showActive = true,
  onShowActiveChange,
  showInactive = true,
  onShowInactiveChange,
  onView,
  onEdit,
  onArchive,
  onToggleActive,
  onRunReport,
  onToggleSubscribe,
  currentUserId,
}) => {
  const [sorting, setSorting] = useState([{ id: 'createdTs', desc: true }]);
  const pendingRuleActionsRef = useRef(new Map());
  const [pendingRuleActions, setPendingRuleActions] = useState(() => new Map());

  useEffect(() => {
    if (isActive) setSorting([{ id: 'createdTs', desc: true }]);
  }, [isActive]);

  const handleRuleAction = async (rule, action, callback) => {
    if (pendingRuleActionsRef.current.has(rule.id)) return;
    if (action === 'status' && rule.archivedTs) return;

    pendingRuleActionsRef.current.set(rule.id, action);
    setPendingRuleActions(new Map(pendingRuleActionsRef.current));
    try {
      await callback(rule);
    } finally {
      pendingRuleActionsRef.current.delete(rule.id);
      setPendingRuleActions(new Map(pendingRuleActionsRef.current));
    }
  };

  return (
    <div className="alarm-rule-table__wrapper" data-testid="alarm-rule-table">
      <MaterialDataTable
        className="event-history-container consist-event-history"
        scrollContainerClassName="consist-event-history__scroll"
        tableTestId="alarm-rule-list-table"
        fitViewport
        ariaLabel="Alarm rule list"
        columns={getColumns({
          onEdit,
          onArchive: rule => handleRuleAction(rule, 'archive', onArchive),
          onToggleActive: rule => handleRuleAction(rule, 'status', onToggleActive),
          onRunReport,
          pendingRuleActions,
        })}
        data={rules}
        isLoading={loadBusy}
        enableSorting
        sorting={sorting}
        onSortingChange={setSorting}
        enableTopToolbar
        enableHiding
        showHideColumnsTestId="alarm-rule-show-hide-columns-button"
        muiTableBodyCellProps={({ row, column }) => ({
          'data-testid': `alarm-rule-cell-${row.original.id}-${column.id}`,
        })}
        topToolbarActions={(
          <div className="alarm-rule-table__filters">
            <span className="alarm-rule-table__filters-label">{'Status:'}</span>
            <label>
              <input
                type="checkbox"
                data-testid="alarm-rule-show-active-checkbox"
                checked={showActive}
                onChange={event => onShowActiveChange?.(event.target.checked)}
              />
              {'Active'}
            </label>
            <label>
              <input
                type="checkbox"
                data-testid="alarm-rule-show-inactive-checkbox"
                checked={showInactive}
                onChange={event => onShowInactiveChange?.(event.target.checked)}
              />
              {'Inactive'}
            </label>
            <span className="alarm-rule-table__filters-divider" />
            <label>
              <input
                type="checkbox"
                data-testid="alarm-rule-show-archived-checkbox"
                checked={showArchived}
                onChange={event => onShowArchivedChange?.(event.target.checked)}
              />
              {'Show archived'}
            </label>
          </div>
        )}
        muiTableBodyRowProps={({ row }) => ({
          'data-testid': `alarm-rule-row-${row.original.id}`,
          className: row.original.archivedTs
            ? 'alarm-rule-table__row--archived alarm-rule-table__row--clickable'
            : 'alarm-rule-table__row--clickable',
          onClick: () => onView(row.original),
        })}
      />
    </div>
  );
};

AlarmRuleTable.propTypes = {
  rules: PropTypes.array,
  isActive: PropTypes.bool,
  loadBusy: PropTypes.bool,
  showArchived: PropTypes.bool,
  onShowArchivedChange: PropTypes.func,
  showActive: PropTypes.bool,
  onShowActiveChange: PropTypes.func,
  showInactive: PropTypes.bool,
  onShowInactiveChange: PropTypes.func,
  onView: PropTypes.func,
  onEdit: PropTypes.func,
  onArchive: PropTypes.func,
  onToggleActive: PropTypes.func,
  onRunReport: PropTypes.func,
  onToggleSubscribe: PropTypes.func,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
